import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { Clear as ClearIcon, Event as EventIcon } from '@material-ui/icons';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import {
  OPEN_ENROLLMENT_FIELDS,
  PLAN_DAY_MAX_DATE,
  PLAN_DAY_MIN_DATE,
  dateToPlanDay,
  planDayToDate,
} from 'Views/Plans/utils/openEnrollmentHelpers';

// Note: Not set (null) keeps the member type's default: customers renew,
// employees are one-time.
const AUTO_RENEW_OPTIONS = [
  { key: 'unset', label: 'Not set', value: null },
  { key: 'yes', label: 'Yes', value: true },
  { key: 'no', label: 'No', value: false },
];

const getAutoRenewKey = (autoRenew, isOnetimePlan) => {
  if (isOnetimePlan || autoRenew === false) return 'no';
  return autoRenew === true ? 'yes' : 'unset';
};

const getAutoRenewHint = (key, isOnetimePlan) => {
  if (isOnetimePlan) return 'One-time plan cannot be renewed';
  if (key === 'unset') return "Not set: customers renew, employees don't";
  return 'Renewal for new subscriptions on this plan';
};

// The picker shows a reference year internally; its calendar header shows
// only the month, since plan days recur every year.
class MonthDayUtils extends DateFnsUtils {
  getCalendarHeaderText(date) {
    return this.format(date, 'MMMM');
  }
}

// A month and day picker with no year, stored as "MM-DD". It looks like the
// Transfers & Payout month picker, with a month then a day view, and has a
// clear button in the field to leave the dates empty.
const PlanDayPicker = ({
  name,
  label,
  value,
  onChange,
  onClear,
  error,
  helperText,
}) => {
  const [open, setOpen] = useState(false);

  const endAdornment = (
    <InputAdornment position="end">
      {value && (
        <Tooltip title="Clear dates">
          <IconButton
            size="small"
            aria-label="clear dates"
            data-testid={`product-${name}-clear`}
            onClick={(event) => {
              event.stopPropagation();
              onClear();
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <IconButton
        aria-label={`change ${label.toLowerCase()}`}
        data-testid={`product-${name}-button`}
        onClick={() => setOpen(true)}
      >
        <EventIcon />
      </IconButton>
    </InputAdornment>
  );

  return (
    <MuiPickersUtilsProvider utils={MonthDayUtils}>
      <DatePicker
        autoOk
        fullWidth
        disableToolbar
        variant="inline"
        inputVariant="outlined"
        views={['month', 'date']}
        openTo="month"
        format="MMMM d"
        name={name}
        id={`product-${name}`}
        label={label}
        value={planDayToDate(value)}
        minDate={PLAN_DAY_MIN_DATE}
        maxDate={PLAN_DAY_MAX_DATE}
        initialFocusedDate={PLAN_DAY_MIN_DATE}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        // Note: Picking the month already reports a date (that month, the
        // current day); picking the day then reports the final one.
        onChange={(date) => onChange(name, dateToPlanDay(date))}
        error={error}
        helperText={helperText}
        placeholder="Select month and day"
        InputLabelProps={{ shrink: true }}
        InputProps={{ endAdornment }}
        inputProps={{ 'data-testid': `product-${name}` }}
      />
    </MuiPickersUtilsProvider>
  );
};

PlanDayPicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.node,
};

const getDatesHint = (autoRenewKey) => {
  if (autoRenewKey === 'yes') {
    return "Leave the dates empty to renew on each subscriber's start-date anniversary.";
  }
  if (autoRenewKey === 'no') {
    return 'Leave the dates empty to end each subscription one year after it starts.';
  }
  return 'Leave the dates empty for terms of a year from each start date.';
};

// SAP-8323: the row Set Open Enrollment reveals, after Plan Attributes.
const OpenEnrollmentFields = ({
  startDay,
  endDay,
  autoRenew,
  isOnetimePlan,
  error,
  showError,
  onChange,
  onClearDates,
}) => {
  const hasError = Boolean(showError && error);
  const autoRenewKey = getAutoRenewKey(autoRenew, isOnetimePlan);

  return (
    <Grid container spacing={3} data-testid="open-enrollment-fields">
      <Grid item xs={12} md={6} lg={4}>
        <PlanDayPicker
          name={OPEN_ENROLLMENT_FIELDS.startDay}
          label="Enrollment Start Date"
          value={startDay}
          onChange={onChange}
          onClear={onClearDates}
          error={hasError}
          helperText={hasError ? error : getDatesHint(autoRenewKey)}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <PlanDayPicker
          name={OPEN_ENROLLMENT_FIELDS.endDay}
          label="Enrollment End Date"
          value={endDay}
          onChange={onChange}
          onClear={onClearDates}
          error={hasError}
          helperText="Start Date fills in as the day after this date"
        />
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <TextField
          select
          fullWidth
          variant="outlined"
          id="product-is_auto_renew"
          label="Auto Renew"
          value={autoRenewKey}
          disabled={isOnetimePlan}
          helperText={getAutoRenewHint(autoRenewKey, isOnetimePlan)}
          onChange={(event) =>
            onChange(
              OPEN_ENROLLMENT_FIELDS.autoRenew,
              AUTO_RENEW_OPTIONS.find(({ key }) => key === event.target.value)
                .value,
            )
          }
          inputProps={{ 'data-testid': 'product-is_auto_renew' }}
        >
          {AUTO_RENEW_OPTIONS.map(({ key, label }) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );
};

OpenEnrollmentFields.propTypes = {
  startDay: PropTypes.string,
  endDay: PropTypes.string,
  // true, false, or null (not set)
  autoRenew: PropTypes.bool,
  isOnetimePlan: PropTypes.bool,
  error: PropTypes.string,
  showError: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onClearDates: PropTypes.func.isRequired,
};

export default OpenEnrollmentFields;
